import { useMemo, useState, useCallback } from 'react';
import {
  Text, Paper, Stack, Group, Table, Skeleton, Drawer,
  SimpleGrid, Divider, ActionIcon, Tooltip, Pagination,
  Button, Modal, TextInput, Select, NumberInput, Switch, Badge,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconChevronUp, IconChevronDown, IconSelector, IconEye, IconReceipt,
  IconPlus, IconPlayerPause, IconPlayerPlay,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import EmptyState from '@/components/EmptyState';
import {
  useMyRecurringOrders,
  useCreateRecurringOrder,
  usePauseRecurringOrder,
  useResumeRecurringOrder,
} from '@/hooks/usePortalApi';
import type { RecurringOrder } from '@/types';

type SortField = 'next_generation_date';
type SortDir = 'asc' | 'desc';
const PAGE_SIZE = 20;

function SortIcon({ field, current, dir }: { field: SortField; current: SortField | null; dir: SortDir }) {
  if (current !== field) return <IconSelector size={14} style={{ opacity: 0.4 }} />;
  return dir === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />;
}

export default function RecurringOrders() {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [drawerItem, setDrawerItem] = useState<RecurringOrder | null>(null);
  const [drawerOpen, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [formName, setFormName] = useState('');
  const [formFrequency, setFormFrequency] = useState<string | null>(null);
  const [formDayOfWeek, setFormDayOfWeek] = useState<number | ''>('');
  const [formDayOfMonth, setFormDayOfMonth] = useState<number | ''>('');
  const [formAutoConfirm, setFormAutoConfirm] = useState(false);

  const { data, isLoading } = useMyRecurringOrders();
  const createRecurringOrder = useCreateRecurringOrder();
  const pauseRecurringOrder = usePauseRecurringOrder();
  const resumeRecurringOrder = useResumeRecurringOrder();
  const allItems = data?.items ?? [];

  const sorted = useMemo(() => {
    if (!sortField) return allItems;
    return [...allItems].sort((a, b) => {
      const cmp = String(a[sortField] ?? '').localeCompare(String(b[sortField] ?? ''));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [allItems, sortField, sortDir]);

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const items = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  };

  const handleView = useCallback((item: RecurringOrder) => { setDrawerItem(item); openDrawer(); }, [openDrawer]);

  const handleCreate = () => {
    if (!formName || !formFrequency) return;
    createRecurringOrder.mutate(
      {
        name: formName,
        frequency: formFrequency,
        day_of_week: (formFrequency === 'weekly' || formFrequency === 'biweekly') && formDayOfWeek !== '' ? formDayOfWeek : undefined,
        day_of_month: formFrequency === 'monthly' && formDayOfMonth !== '' ? formDayOfMonth : undefined,
        auto_confirm: formAutoConfirm,
        items: [],
      },
      {
        onSuccess: () => {
          notifications.show({ title: 'Created', message: 'Recurring order created successfully', color: 'green' });
          setCreateOpen(false);
          setFormName('');
          setFormFrequency(null);
          setFormDayOfWeek('');
          setFormDayOfMonth('');
          setFormAutoConfirm(false);
        },
        onError: () => {
          notifications.show({ title: 'Error', message: 'Failed to create recurring order', color: 'red' });
        },
      },
    );
  };

  const handlePause = (id: number) => {
    pauseRecurringOrder.mutate(id, {
      onSuccess: () => notifications.show({ title: 'Paused', message: 'Recurring order paused', color: 'yellow' }),
      onError: () => notifications.show({ title: 'Error', message: 'Failed to pause order', color: 'red' }),
    });
  };

  const handleResume = (id: number) => {
    resumeRecurringOrder.mutate(id, {
      onSuccess: () => notifications.show({ title: 'Resumed', message: 'Recurring order resumed', color: 'green' }),
      onError: () => notifications.show({ title: 'Error', message: 'Failed to resume order', color: 'red' }),
    });
  };

  const ThSort = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <Table.Th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(field)}>
      <Group gap={4} wrap="nowrap">{children}<SortIcon field={field} current={sortField} dir={sortDir} /></Group>
    </Table.Th>
  );

  return (
    <Stack gap="lg">
      <PageHeader
        title="Recurring Orders"
        description="Manage your recurring order schedules"
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateOpen(true)}>
            New Recurring Order
          </Button>
        }
      />
      {isLoading ? (
        <Stack gap="xs">{[...Array(5)].map((_, i) => <Skeleton key={i} height={50} radius="md" />)}</Stack>
      ) : items.length === 0 ? (
        <EmptyState icon={<IconReceipt size={30} />} title="No recurring orders" description="No recurring orders found" />
      ) : (
        <>
          <Paper>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Frequency</Table.Th>
                  <ThSort field="next_generation_date">Next Generation Date</ThSort>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Auto Confirm</Table.Th>
                  <Table.Th w={120} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((item: RecurringOrder) => (
                  <Table.Tr key={item.id}>
                    <Table.Td><Text size="sm" fw={500}>{item.name}</Text></Table.Td>
                    <Table.Td><Text size="sm" tt="capitalize">{item.frequency}</Text></Table.Td>
                    <Table.Td><Text size="sm">{item.next_generation_date ?? '—'}</Text></Table.Td>
                    <Table.Td><StatusBadge status={item.status ?? 'active'} /></Table.Td>
                    <Table.Td>
                      <Badge color={item.auto_confirm ? 'green' : 'gray'} variant="light" size="sm">
                        {item.auto_confirm ? 'Yes' : 'No'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <Tooltip label="View"><ActionIcon variant="subtle" size="sm" onClick={() => handleView(item)}><IconEye size={16} /></ActionIcon></Tooltip>
                        {item.status === 'active' && (
                          <Tooltip label="Pause">
                            <ActionIcon variant="subtle" size="sm" color="yellow" onClick={() => handlePause(item.id)} loading={pauseRecurringOrder.isPending}>
                              <IconPlayerPause size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        {item.status === 'paused' && (
                          <Tooltip label="Resume">
                            <ActionIcon variant="subtle" size="sm" color="green" onClick={() => handleResume(item.id)} loading={resumeRecurringOrder.isPending}>
                              <IconPlayerPlay size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
          {totalPages > 1 && <Group justify="center"><Pagination total={totalPages} value={page} onChange={setPage} /></Group>}
        </>
      )}
      <Drawer opened={drawerOpen} onClose={closeDrawer} position="right" size="lg" title={<Text fw={700} size="lg">Recurring Order Details</Text>}>
        {drawerItem && (
          <Stack gap="md">
            <StatusBadge status={drawerItem.status ?? 'active'} />
            <Divider />
            <SimpleGrid cols={2}>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Name</Text><Text size="sm" fw={500}>{drawerItem.name}</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Frequency</Text><Text size="sm" fw={500}>{drawerItem.frequency}</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Next Generation Date</Text><Text size="sm" fw={500}>{drawerItem.next_generation_date ?? '—'}</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Auto Confirm</Text><Text size="sm" fw={500}>{drawerItem.auto_confirm ? 'Yes' : 'No'}</Text></div>
            </SimpleGrid>
          </Stack>
        )}
      </Drawer>

      <Modal opened={createOpen} onClose={() => setCreateOpen(false)} title="New Recurring Order" size="md">
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="e.g. Weekly Produce Order"
            value={formName}
            onChange={(e) => setFormName(e.currentTarget.value)}
            required
          />
          <Select
            label="Frequency"
            placeholder="Select frequency"
            data={[
              { value: 'daily', label: 'Daily' },
              { value: 'weekly', label: 'Weekly' },
              { value: 'biweekly', label: 'Biweekly' },
              { value: 'monthly', label: 'Monthly' },
            ]}
            value={formFrequency}
            onChange={setFormFrequency}
            required
          />
          {(formFrequency === 'weekly' || formFrequency === 'biweekly') && (
            <NumberInput
              label="Day of Week"
              description="0 = Monday, 6 = Sunday"
              min={0}
              max={6}
              value={formDayOfWeek}
              onChange={(val) => setFormDayOfWeek(typeof val === 'number' ? val : '')}
            />
          )}
          {formFrequency === 'monthly' && (
            <NumberInput
              label="Day of Month"
              description="1–28"
              min={1}
              max={28}
              value={formDayOfMonth}
              onChange={(val) => setFormDayOfMonth(typeof val === 'number' ? val : '')}
            />
          )}
          <Switch
            label="Auto Confirm"
            description="Automatically confirm generated orders"
            checked={formAutoConfirm}
            onChange={(e) => setFormAutoConfirm(e.currentTarget.checked)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} loading={createRecurringOrder.isPending} disabled={!formName || !formFrequency}>
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
